// client/src/features/quotations/sourceLabel.ts
//
// "Invoice" / "Historical" / "Price List" / "Catalogue" — the badge a row's
// raw `source` field (`VendorInvoice`, `historical_quotation`, `PriceList`,
// `MasterCatalogue`, …) turns into on screen.
//
// Extracted out of `components/SupplierDropdown.tsx`, where it was a private,
// unexported function, so `ItemLookupPanel` — the merged catalogue+supplier
// lookup panel that replaces `SupplierDropdown` and
// `MasterCatalogueSearchPopup` — can call the SAME reading for its own Source
// column instead of growing a second copy (the golden rule: one owner per
// business rule, no matter how small). `SupplierDropdown` now imports this
// instead of defining its own; behaviour is unchanged —
// `SupplierDropdown.test.tsx` still pins every case below.

export function formatSourceLabel(source?: string): { label: string; classes: string } {
  // Normalize to a key: lowercase + strip non-alphanumerics so PascalCase
  // (`VendorInvoice`), snake_case (`vendor_invoice`) and spaced
  // (`Vendor Invoice`) variants all collapse to the same value.
  const key = String(source || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  switch (key) {
    // 'invoice' / 'added' / 'priceedit' are `priceDateBasis` values (see
    // `priceSourceLabel` below).
    case 'invoice':
    case 'vendorinvoice':
      return { label: 'Invoice', classes: 'bg-orange-50 text-orange-700 border-orange-200' };
    case 'historicalquotation':
    case 'quotationhistorical':
      return { label: 'Historical', classes: 'bg-indigo-50 text-indigo-700 border-indigo-200' };
    case 'pricelist':
      return { label: 'Price List', classes: 'bg-cyan-50 text-cyan-700 border-cyan-200' };
    // A hand-entered Master Catalogue row — a supplier somebody typed in, which
    // by definition appears on no invoice, quote or price list (QUO-00017).
    // Labelled for where it came from, not for the collection's name.
    // `MasterCatalogueItem.source` (server model) spells the exact same fact
    // 'manual' instead of 'MasterCatalogue' — a person typed the row in
    // directly. Added for `SupplierDetailsCard`, the first caller to feed
    // this function a raw catalogue-ROW source rather than the merged
    // lookup/pricing vocabulary above; same fact, same label, one owner.
    case 'mastercatalogue':
    case 'manual':
    case 'added':
    case 'priceedit':
      return { label: 'Catalogue', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    // The other `MasterCatalogueItem.source` value with no invoice, quote or
    // price list behind it either — a row whose origin was never recorded.
    case 'unknown':
      return { label: 'Unknown', classes: 'bg-slate-50 text-slate-500 border-slate-200' };
    case '':
      return { label: '—', classes: 'bg-slate-50 text-slate-500 border-slate-200' };
    default:
      // Insert spaces before capitals for readable fallback (e.g. `SomethingElse` → `Something Else`).
      const fallback = String(source).replace(/([a-z])([A-Z])/g, '$1 $2');
      return { label: fallback, classes: 'bg-slate-50 text-slate-700 border-slate-200' };
  }
}

/**
 * Where a PRICE came from, for a catalogue row or a supplier option alike: what
 * dated it (`priceDateBasis`, server `priceDateFieldsOf` — catalogue-backed only),
 * else its `source`. A price-list or typed catalogue row repriced by an invoice
 * keeps its `source`, so reading `source` alone named an invoice price
 * "Catalogue" beside the invoice's own date (QUO-00071).
 */
export function priceSourceLabel(row: { priceDateBasis?: string | null; source?: string | null }) {
  return formatSourceLabel(row.priceDateBasis || row.source || undefined);
}
